import React, { useRef } from 'react';
import CheckmarkIcon from '@moda/icons/checkmark-16';
import HelpIcon from '@moda/icons/help-16';
import { Stack, Button, Text, Popover } from '@moda/om';
import { hrefFor } from '../../routers';
import { formatPrice } from '../../lib/formatPrice';
import { useClickOutside } from '../../hooks/useClickOutside';
import { useCart, useCartItems } from '../../hooks/useCart';
import { LineItem } from '../../hooks/useCart/cart.rest';
import { Image } from '../Image';
import { LineItemList } from '../LineItemList';
import { useBagVisitTracking } from '../../hooks/useBagVisitTracking';

import './MiniCartDropdown.scss';

interface Props {
  justAddedItem: LineItem | null;
  onHide: () => void;
  onHover: () => void;
}

export const MiniCartDropdown: React.FC<Props> = ({ justAddedItem, onHide, onHover }) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const { cart } = useCart();

  useBagVisitTracking(justAddedItem !== null);

  const {
    cartItems,
    availableNowItems,
    preorderItems,
    unavailableItems,
    invalidItems,
    totalUnavailable
  } = useCartItems();
  const { subtotal, estimatedTaxes, payToday } = cart.attributes.paymentSummary;

  useClickOutside(ref, onHide);

  return (
    <div
      ref={ref}
      className="MiniCartDropdown"
      onMouseEnter={onHover}
      onMouseLeave={onHide}
      data-testid="miniCartDropdown"
    >
      {cartItems.length === 0 && (
        <Text className="MiniCartDropdown__empty-message" treatment="h6" color="cement">
          There are currently no items in your bag.
        </Text>
      )}
      {cartItems.length > 0 && (
        <>
          {justAddedItem ? (
            <Stack className="MiniCartDropdown__just-added-container" space={3}>
              <Stack
                className="MiniCartDropdown__just-added-header"
                space={3}
                direction="horizontal"
              >
                <CheckmarkIcon
                  width="2rem"
                  height="2rem"
                  className="MiniCartDropdown__just-added-checkmark"
                />
                <Text treatment="h4" family="serif">
                  Item Added
                </Text>
              </Stack>
              <Image
                className="MiniCartDropdown__just-added-product-image"
                src={justAddedItem.attributes.imageSrc}
                alt={justAddedItem.attributes.productName}
              />
            </Stack>
          ) : (
            <div className="MiniCartDropdown__items">
              {availableNowItems.length > 0 && (
                <LineItemList size="mini" title="Available Now" items={availableNowItems} />
              )}
              {preorderItems.length > 0 && (
                <LineItemList size="mini" title="Preorder" items={preorderItems} />
              )}
              {(invalidItems.length > 0 || unavailableItems.length > 0) && (
                <LineItemList size="mini" title="Unavailable" items={totalUnavailable} />
              )}
            </div>
          )}
          <div className="MiniCartDropdown__summary">
            <Stack space={3}>
              <div className="MiniCartDropdown__summary-row">
                <Text treatment="body1" color="cement">
                  Order Subtotal
                </Text>
                <Text treatment="body1" color="cement">
                  {formatPrice(subtotal.currency, subtotal.price)}
                </Text>
              </div>
              {estimatedTaxes.price > 0 && (
                <div className="MiniCartDropdown__summary-row">
                  <Text treatment="body1" color="cement">
                    Est. Taxes/Duties
                  </Text>
                  <Text treatment="body1" color="cement">
                    {formatPrice(estimatedTaxes.currency, estimatedTaxes.price)}
                  </Text>
                </div>
              )}
              <div className="MiniCartDropdown__summary-row">
                <Stack className="MiniCartDropdown__pay-today" space={1} direction="horizontal">
                  <Text treatment="h6">Est. Pay Today </Text>
                  <Popover
                    anchor="bottomCenter"
                    content={
                      <div className="MiniCartDropdown__preorder-popover">
                        Preorder items require a 50% deposit at the time of purchase. The remaining
                        balance and sales tax/duties will be charged upon shipping.
                      </div>
                    }
                  >
                    <HelpIcon />
                  </Popover>
                </Stack>
                <Text treatment="h6">{formatPrice(payToday.currency, payToday.price)}</Text>
              </div>
              <Button
                className="MiniCartDropdown__bag-button"
                data-testid="miniCartDropdownButton"
                to={hrefFor.ShoppingBagPage()}
                onClick={onHide}
              >
                Go to my Shopping Bag
              </Button>
            </Stack>
          </div>
        </>
      )}
    </div>
  );
};
